// @owners { team: patients }
import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoSpinningLoader,
  ButtonSelector,
  Card,
  type DateData,
  Description,
  Divider,
  H2,
  Icon,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  ListHeader,
  ListItem,
  type OptionValue,
  Row,
  SmSpacing,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { type ShipmentFeeCohort } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_cohort';
import { type ShipmentFeeType } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_type';
import {
  type FulfillmentOption,
  FulfillmentOptionTagMap,
  type FulfillmentWindowOption,
} from '@alto/scriptdash/alto/patient_app/checkout/v1/checkout_endpoint';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { updateOrder } from '~shared/actions/checkout';
import { getAddress } from '~shared/features/addresses/selectors/getAddress';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { useCheckoutDateErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { usePrescriptionIDsForCurrentOrder } from '~shared/features/checkout/hooks/usePrescriptionIDsForCurrentOrder';
import {
  getEditShipmentID,
  getShowingValidationErrors,
  getWindows,
} from '~shared/features/checkout/selectors/getCheckout';
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import {
  getFirstOrderDate,
  getIsOrderPickupMethod,
  getOrderAddressId,
  getOrderDate,
  getOrderDeliverAfter,
  getOrderDeliverBefore,
  getOrderIsAsap,
} from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import getSelectedPaymentTypes from '~shared/features/checkout/selectors/getSelectedPaymentTypes';
import { getShipmentPaymentMethodsByShipmentID } from '~shared/features/payments/selectors/getShipmentPaymentMethods';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { getIsGskForYouUser } from '~shared/features/users/selectors/getIsGskForYouUser';
import { formatDateStringForAPI, formatWindowTime } from '~shared/helpers/date';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { isCourier, isMail } from '~shared/helpers/order';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { useGetPaymentMethod } from '~shared/queries/query-keys/paymentMethods';
import { exitOrderBundling, initializeOrderBundling } from '~shared/reducers/ui/checkoutFlow';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import {
  SelectDeliveryWindowActionSheet,
  getOrderBundlingCheckoutOrigin,
  useOrderBundlingOptions,
  useQueryFulfillmentOptions,
} from '../../../..';
import { getSelectedPriceTypesForPrescriptions } from '../../helpers/getSelectedPriceTypesForPrescriptions';
import { AddressInfo } from '../delivery-address-card/address-info/AddressInfo';
import { AddressInfoAlert } from '../delivery-address-card/address-info/AddressInfoAlert';
import { FeeRadioRightContent } from '../select-date-card/FeeRadioRightContent';
import { FulfillmentOptionRadioOption } from './FulfillmentOptionRadioOption';
import { GskSpeedUpSection } from './GskSpeedUpSection';
import { PickupInfo } from './PickupInfo';
import { PickupOptionsSection } from './PickupOptionsSection';
import { SelectCustomDateActionSheet } from './SelectCustomDateActionSheet';
import {
  deriveCustomFulfillmentOption,
  getCheckedValueFromOrder,
  getFulfillmentDeliveryFeeTypeFlags,
  getRadioDateOptions,
  resolveSelectedFulfillmentWindowOption,
} from './utils';

type Props = {
  readonly openGetItSoonerFlow: () => void;
};

export const DELIVERY_VALUE = 'Delivery';
export const PICKUP_VALUE = 'Pickup';
export type DeliveryMethodToggleOptions = typeof DELIVERY_VALUE | typeof PICKUP_VALUE;

const pickupToggleOptions = [
  { label: DELIVERY_VALUE, value: DELIVERY_VALUE, testID: 'checkout-delivery' },
  { label: PICKUP_VALUE, value: PICKUP_VALUE, testID: 'checkout-pickup' },
];

const isWeb = getPlatformOS() === 'web';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const SchedulingCard = ({ openGetItSoonerFlow }: Props) => {
  const dispatch = useDispatchShared();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const { value: isPickupToggleEnabled } = Experimentation.useFeatureFlag('scheduling_module_pickup_toggle');

  // order selectors
  const orderDate = useSelectorShared(getOrderDate);
  const isAsap = useSelectorShared(getOrderIsAsap);
  const firstOrderDate = useSelectorShared(getFirstOrderDate);
  const isOrderPickupMethod = useSelectorShared(getIsOrderPickupMethod);
  const orderDeliverAfter = useSelectorShared(getOrderDeliverAfter);
  const orderDeliverBefore = useSelectorShared(getOrderDeliverBefore);
  const addressID = useSelectorShared(getOrderAddressId);
  const address = useSelectorShared((state) => getAddress(state, { addressID }));
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  const checkedValue = getCheckedValueFromOrder({ orderDate, isOrderPickupMethod, isOrderBundling });

  // other selectors
  const isGskForYouUser = useSelectorShared(getIsGskForYouUser);
  const { gskForYouHasOnlyStandardMailFee } = useQueryShipmentFeesAvailable();
  const saveOrderLoading = useSelectorShared((state) => state.ui.loading.saveOrderLoading);
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const { dateValidationErrors } = useCheckoutDateErrors();
  const { prescriptionIDs: cartPrescriptionIDs } = usePrescriptionsInCart();
  const cartSelectedPaymentTypes = useSelectorShared(getSelectedPaymentTypes);
  const selectedPrices = getSelectedPriceTypesForPrescriptions({
    prescriptionIDs: cartPrescriptionIDs,
    cartSelectedPaymentTypes,
  });
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const orderBundlingOption = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const {
    canCombine,
    earliestUpcomingShipment,
    isLoading: isOrderBundlingOptionsLoading,
  } = useOrderBundlingOptions({
    address_id: addressID,
    origin: orderBundlingOption,
  });
  const earliestUpcomingShipmentPaymentMethods = useSelectorShared((state) =>
    getShipmentPaymentMethodsByShipmentID(state, { shipmentID: earliestUpcomingShipment?.id }),
  );
  const earliestUpcomingShipmentPaymentMethodId = earliestUpcomingShipmentPaymentMethods[0]?.payment_method_id;
  const earliestUpcomingShipmentSecondPaymentMethodId = earliestUpcomingShipmentPaymentMethods[1]?.payment_method_id;
  const isEarliestUpcomingShipmentPaymentMethodIdApplePay =
    useGetPaymentMethod({
      paymentMethodID: earliestUpcomingShipmentPaymentMethodId,
      enabled: !!earliestUpcomingShipmentPaymentMethodId,
    })?.paymentMethod?.details?.native_pay_type === 'apple_pay';
  const editShipmentId = useSelectorShared(getEditShipmentID);

  const { deliveryMethods } = useCartDeliveryMethods();
  const orderIndex = useSelectorShared(getOrderIndex);
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];
  const isFirstShipment = orderIndex === 0;
  const prescriptionIDsForCurrentOrder = usePrescriptionIDsForCurrentOrder();

  const selectedPricesForCurrentOrder =
    deliveryMethods.length <= 1
      ? selectedPrices
      : (selectedPrices ?? []).filter((p) => prescriptionIDsForCurrentOrder.includes(p.prescription_id));

  const isMailOrder = isMail(deliveryMethod?.mode);
  const isCourierOrder = isCourier(deliveryMethod?.mode);

  const {
    courierDateFulfillmentOptions,
    courierAnyDateFulfillmentOption,
    mailFulfillmentOptions,
    mailAnyDateFulfillmentOption,
    pickupFulfillmentOptions,
    isLoading: isFulfillmentOptionsLoading,
    isSuccess: isFulfillmentOptionsSuccess,
  } = useQueryFulfillmentOptions({
    addressID,
    cartPrescriptionIDs: prescriptionIDsForCurrentOrder,
    selectedPrices: selectedPricesForCurrentOrder,
    shipmentID: editShipmentId,
  });
  const hasAvailablePickupOptions = pickupFulfillmentOptions.length > 0;
  const showPickupToggle = isPickupToggleEnabled && hasAvailablePickupOptions;
  const showDeliveryOptions = !isPickupToggleEnabled || !isOrderPickupMethod || !showPickupToggle;
  const ready_by_time = pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time;
  const orderBundlingDates = courierDateFulfillmentOptions
    .filter((option) => option.tag === FulfillmentOptionTagMap.BUNDLED)
    .map((option) => option.date)
    .filter(TypescriptUtils.isPresent);

  const courierDateFulfillmentOptionsForDisplay = canCombine
    ? courierDateFulfillmentOptions
    : courierDateFulfillmentOptions.filter((option) => option.tag !== FulfillmentOptionTagMap.BUNDLED);

  // Get any applicable fees for any day case - used when selecting custom dates
  const anyDateOption = isMailOrder ? mailAnyDateFulfillmentOption : courierAnyDateFulfillmentOption;

  const fulfillmentOptionsForWindowLookup = [...courierDateFulfillmentOptionsForDisplay, ...mailFulfillmentOptions];

  const selectedFulfillmentWindowOption = resolveSelectedFulfillmentWindowOption({
    orderDate,
    orderDeliverAfter,
    orderDeliverBefore,
    isAsap,
    fulfillmentOptions: fulfillmentOptionsForWindowLookup,
  });

  const customFulfillmentOption = (() => {
    if (isFulfillmentOptionsLoading || !isFulfillmentOptionsSuccess || !orderDate) return undefined;

    let baseOptions = courierDateFulfillmentOptionsForDisplay;
    if (isMailOrder) {
      baseOptions = mailFulfillmentOptions;
    }

    return deriveCustomFulfillmentOption({
      orderDate,
      baseOptions,
      anyDateOption,
      selectedFulfillmentWindowOption,
    });
  })();

  const radioDateOptions = getRadioDateOptions({
    courierDateFulfillmentOptions: courierDateFulfillmentOptionsForDisplay,
    mailFulfillmentOptions: isMailOrder ? mailFulfillmentOptions : [],
    customFulfillmentOption,
    isSecondSplitShipment: orderIndex > 0,
    selectedDate: orderDate,
    isMailOrder,
  });

  const disableSelector = saveOrderLoading || isFulfillmentOptionsLoading || isOrderBundlingOptionsLoading;
  const showErrors = showingValidationErrors && dateValidationErrors?.length;
  const errorMessage = dateValidationErrors.map((e) => e.message).join('. ');

  let cardTitle: undefined | string = undefined;

  // Scheduling card title will be 'Schedule delivery' when pickup toggle is not enabled
  // When pickup toggle is enabled:
  //  1. Card title will be 'Select delivery date' if NO pickup options are available
  //  2. When pickup options are available, a Header will be displayed under the toggle selector to replace the card title
  if (!isPickupToggleEnabled) {
    cardTitle = 'Schedule delivery';
  } else if (!hasAvailablePickupOptions) {
    cardTitle = 'Select delivery date';
  }

  // if pickup toggle was enabled and then the app was backgrounded, check if pickup options are still available when the app is foregrounded
  // if not, reset order state
  useEffect(() => {
    if (!hasAvailablePickupOptions && showPickupToggle && !isFulfillmentOptionsLoading) {
      dispatch(
        updateOrder({
          date: undefined,
          delivery_method: undefined,
          pickup_address: undefined,
          pickup_ready_time: undefined,
          pay_at_pickup: undefined,
          home_to_sign_confirmation: undefined,
          deliver_after: undefined,
          deliver_before: undefined,
        }),
      );
    }
  }, [hasAvailablePickupOptions, showPickupToggle, dispatch, isFulfillmentOptionsLoading]);

  useEffect(() => {
    if (!isFulfillmentOptionsSuccess) return;

    const asapFulfillmentOption = courierDateFulfillmentOptions.find((fulfillmentOption) => {
      return fulfillmentOption.available_fees.some((fee) => fee.fee_type === 'asap_delivery');
    });

    const fulfillmentDeliveryFeeTypeFlags = getFulfillmentDeliveryFeeTypeFlags([
      ...courierDateFulfillmentOptions,
      ...mailFulfillmentOptions,
      courierAnyDateFulfillmentOption,
      mailAnyDateFulfillmentOption,
    ]);

    trackEvent({
      event: EVENTS.CHECKOUT_VIEWED_WITH_FULFILLMENT_OPTIONS,
      params: {
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        shipment_id: isEditingOrder ? editShipmentId : null,
        asap_eligible: !!asapFulfillmentOption,
        // only populated when one courier window option for asap delivery
        asap_eta: asapFulfillmentOption?.fulfillment_window_option?.estimated_eta,
        pickup_eligible: pickupFulfillmentOptions.length > 0,
        pickup_eta: pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time,
        is_gsk_for_you_user: isGskForYouUser,
        gsk_for_you_has_only_standard_mail_fee: gskForYouHasOnlyStandardMailFee,
        ...fulfillmentDeliveryFeeTypeFlags,
      },
    });
  }, [
    trackEvent,
    isFulfillmentOptionsSuccess,
    courierDateFulfillmentOptions,
    courierAnyDateFulfillmentOption,
    mailFulfillmentOptions,
    mailAnyDateFulfillmentOption,
    isEditingOrder,
    editShipmentId,
    pickupFulfillmentOptions,
    gskForYouHasOnlyStandardMailFee,
    isGskForYouUser,
  ]);

  const onMountRef = useRef(true);
  useEffect(() => {
    // this effect should only run when the address changes, not on mount
    if (onMountRef.current) {
      onMountRef.current = false;
      return;
    }

    // Whenever address changes or address safe place changes clear out
    // order scheduling selections as they may not be applicable.
    dispatch(
      updateOrder({
        date: undefined,
        deliver_after: undefined,
        deliver_before: undefined,
        is_asap: undefined,
      }),
    );
  }, [address?.safe_place, addressID, dispatch]);

  // Whenever pickup is selected and the pickup_ready_time changes, update the pickup_ready_time in redux
  useEffect(() => {
    if (!isOrderPickupMethod) return;

    dispatch(updateOrder({ pickup_ready_time: ready_by_time }));
  }, [dispatch, isOrderPickupMethod, ready_by_time]);

  // When entering the second order of a split shipment, pre-fill date from first order only if second order has no date yet.
  // Do not overwrite when the user has already selected a date (e.g. a mail date different from first order).
  // Set delivery_method for mail so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
  useEffect(() => {
    if (orderIndex > 0 && firstOrderDate && !orderDate) {
      const updatePayload =
        orderIndex === 1 && isMailOrder
          ? { date: firstOrderDate, delivery_method: deliveryMethod?.name }
          : { date: firstOrderDate };
      dispatch(updateOrder(updatePayload));
    }
  }, [deliveryMethod?.name, dispatch, firstOrderDate, isMailOrder, orderIndex, orderDate]);

  // Once the second order date is set, auto-select the first delivery window after windows are fetched
  // via cart middleware and in the redux store
  const windows = useSelectorShared(getWindows);
  const deliver_after = windows[0]?.deliver_after;
  const deliver_before = windows[0]?.deliver_before;
  useEffect(() => {
    if (orderIndex > 0 && isCourierOrder && deliver_after && deliver_before) {
      dispatch(updateOrder({ deliver_after, deliver_before }));
    }
  }, [deliver_after, deliver_before, dispatch, isCourierOrder, orderIndex]);

  // if date options do not have fees, no order bundling option, and one window available,
  // auto-select the earliest date option when entering checkout and set the order date and window
  useEffect(() => {
    // If a date is already set or pickup is selected, do not pre-select a date.
    if (orderDate || isOrderPickupMethod) return;

    const dateOptions = isMailOrder ? mailFulfillmentOptions : courierDateFulfillmentOptions;

    const hasFees = dateOptions.some((option) => option.available_fees.length > 0);
    const hasOrderBundling = dateOptions.some((option) => option.tag === 'BUNDLED');
    if (hasFees || hasOrderBundling) return;

    const earliestOption = dateOptions[0];
    if (!earliestOption) return;
    // when there's only one window it will be populated in the fulfillment_window_option
    const { date, fulfillment_window_option: option } = earliestOption;
    if (!date) return;
    const formattedDate = formatDateStringForAPI(date);

    // If the delivery method is mail and no pickup option is selected, default to the first date available.
    // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
    if (isMailOrder && !isOrderPickupMethod) {
      dispatch(updateOrder({ date: formattedDate, delivery_method: deliveryMethod?.name }));
      return;
    }

    // Only pre-select if a window option exists for that date.
    if (!option?.delivery_window) return;

    const { start_time, end_time } = option.delivery_window;
    dispatch(
      updateOrder({
        date: formattedDate,
        deliver_after: start_time,
        deliver_before: end_time,
        delivery_method: undefined,
      }),
    );
  }, [
    courierDateFulfillmentOptions,
    deliveryMethod?.mode,
    deliveryMethod?.name,
    dispatch,
    isMailOrder,
    isOrderPickupMethod,
    mailFulfillmentOptions,
    orderDate,
  ]);

  const trackWindowSelected = ({
    autoSelected,
    deliver_after,
    deliver_before,
    fee_type,
    fee_cohort,
  }: {
    autoSelected?: boolean;
    deliver_after?: string;
    deliver_before?: string;
    fee_type?: ShipmentFeeType | null;
    fee_cohort?: ShipmentFeeCohort | null;
  }) => {
    trackEvent({
      event: EVENTS.CHECKOUT__WINDOW_SELECTED,
      params: {
        'auto-selected': autoSelected ?? false,
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        deliver_after,
        deliver_before,
        fee_type,
        fee_cohort,
      },
    });
  };

  const trackMailSelected = ({
    fee_type,
    fee_cohort,
    selected_date,
  }: {
    fee_type?: ShipmentFeeType | null;
    fee_cohort?: ShipmentFeeCohort | null;
    selected_date: string;
  }) => {
    trackEvent({
      event: EVENTS.CHECKOUT__MAIL_SELECTED,
      params: {
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        fee_type,
        fee_cohort,
        selected_date,
      },
    });
  };

  const handleCustomDateDeliveryWindowSelection = (deliveryDate: string, deliveryWindow: FulfillmentWindowOption) => {
    const deliver_after = deliveryWindow.delivery_window?.start_time;
    const deliver_before = deliveryWindow.delivery_window?.end_time;
    const isAsap = deliveryWindow.available_fee?.fee_type === 'asap_delivery';

    trackWindowSelected({
      deliver_after,
      deliver_before,
      fee_type: deliveryWindow.available_fee?.fee_type,
      fee_cohort: deliveryWindow.available_fee?.fee_cohort,
    });

    dispatch(
      updateOrder({
        date: deliveryDate,
        deliver_after,
        deliver_before,
        delivery_method: deliveryMethod?.name || null,
        is_asap: isAsap,
      }),
    );
    // If the user selects any other date, then it cannot be an order bundling
    // option, so we clear any previous order bundling state
    dispatch(exitOrderBundling());
    closeActionSheet();
  };

  const handleCustomDateSelection = (date: DateData) => {
    const deliveryDate = formatDateStringForAPI(new Date(date.timestamp).toISOString());

    // If delivery method is mail, then delivery windows are not
    // applicable. Set order date and do not show window selection modal.
    if (isMailOrder) {
      closeActionSheet();

      const mailFee = anyDateOption?.available_fees?.[0];
      trackMailSelected({
        fee_type: mailFee?.fee_type,
        fee_cohort: mailFee?.fee_cohort,
        selected_date: deliveryDate,
      });

      // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
      dispatch(updateOrder({ date: deliveryDate, delivery_method: deliveryMethod?.name }));
      return;
    }

    setActiveActionSheet(
      <SelectDeliveryWindowActionSheet
        deliveryDate={deliveryDate}
        selectedWindowOption={selectedFulfillmentWindowOption}
        handleCustomDateDeliveryWindowSelection={handleCustomDateDeliveryWindowSelection}
      />,
    );
  };

  const handleStandardDeliveryWindowSelection = (deliveryWindow: FulfillmentWindowOption) => {
    const isAsap = deliveryWindow?.available_fee?.fee_type === 'asap_delivery';
    let deliver_after: string | undefined;
    let deliver_before: string | undefined;
    if (isAsap) {
      deliver_after = formatWindowTime({
        windowTime: new Date(),
        meridiem: 'a',
        forceReturnMinutes: true,
        useUTC: false,
        separator: ' ',
      });
      deliver_before = formatWindowTime({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        windowTime: deliveryWindow.estimated_eta!,
        meridiem: 'a',
        forceReturnMinutes: true,
        useUTC: false,
        separator: ' ',
      });
    } else {
      deliver_after = deliveryWindow.delivery_window?.start_time;
      deliver_before = deliveryWindow.delivery_window?.end_time;
    }

    trackWindowSelected({
      deliver_after,
      deliver_before,
      fee_type: deliveryWindow.available_fee?.fee_type,
      fee_cohort: deliveryWindow.available_fee?.fee_cohort,
    });

    dispatch(
      updateOrder({
        date: formatDateStringForAPI(deliveryWindow.date),
        deliver_before,
        deliver_after,
        delivery_method: deliveryMethod?.name || null,
        is_asap: isAsap,
      }),
    );
    closeActionSheet();
  };

  const selectPickUpOption = useCallback(
    (dateKey: string) => {
      const selectedDate = dateKey.replace('PICKUP-', '');
      const selectedOption = pickupFulfillmentOptions[0];

      trackEvent({
        event: EVENTS.CHECKOUT__PICKUP_SELECTED,
        params: {
          origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
          ready_by_time: selectedOption?.pickup_data?.ready_by_time,
        },
      });
      dispatch(
        updateOrder({
          date: selectedDate,
          tip_amount: undefined,
          delivery_method: 'pickup',
          deliver_after: undefined,
          deliver_before: undefined,
          pickup_address: pickupFulfillmentOptions[0]?.pickup_data?.facility_address,
          pickup_ready_time: pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time,
        }),
      );
    },
    [dispatch, isEditingOrder, pickupFulfillmentOptions, trackEvent],
  );

  const selectBundledOption = useCallback(
    (dateKey: string, selectedOption: FulfillmentOption) => {
      if (!canCombine) {
        return;
      }

      const selectedDate = dateKey.replace(`-${FulfillmentOptionTagMap.BUNDLED}`, '');
      dispatch(initializeOrderBundling());

      const { start_time, end_time } = selectedOption?.fulfillment_window_option?.delivery_window ?? {};
      dispatch(
        updateOrder({
          date: selectedDate,
          deliver_after: start_time,
          deliver_before: end_time,
          tip_amount: undefined,
          delivery_method: deliveryMethod?.name || null,
          pay_with_apple_pay: isEarliestUpcomingShipmentPaymentMethodIdApplePay,
          payment_method_id: earliestUpcomingShipmentPaymentMethodId,
          second_payment_method_id: earliestUpcomingShipmentSecondPaymentMethodId,
        }),
      );
    },
    [
      canCombine,
      deliveryMethod?.name,
      dispatch,
      earliestUpcomingShipmentPaymentMethodId,
      earliestUpcomingShipmentSecondPaymentMethodId,
      isEarliestUpcomingShipmentPaymentMethodIdApplePay,
    ],
  );

  const handleRadioPress = (dateKey: string) => {
    // It is possible to have multiple options with the same date if one of them is
    // bundled and the other is not. To account for this we structure the date key
    // to be MM-DD-YYYY and append -${tag} if a tag (like bundled) is applicable.
    const selectedDate = dateKey;

    const isPickupOption = dateKey.startsWith('PICKUP');
    if (isPickupOption) {
      selectPickUpOption(dateKey);
      return;
    }

    // Clearing delivery method when switching to a different non-pickup option would cause pricing to use 'courier' default.
    // We keep setting delivery_method from the selected option below so pricing returns correct fees.
    const selectedOption = [
      ...courierDateFulfillmentOptionsForDisplay,
      ...mailFulfillmentOptions,
      customFulfillmentOption,
    ].find((option) => {
      const tagSuffix = option?.tag ? `-${option.tag}` : '';
      return `${option?.date}${tagSuffix}` === dateKey;
    });

    const isBundledOption = dateKey.includes(FulfillmentOptionTagMap.BUNDLED);
    if (isBundledOption) {
      // Bundled options are hidden when canCombine is false; still bail here if Redux
      // order state is stale (e.g. isOrderBundling + -BUNDLED checked value while prescriptions empty).
      if (selectedOption && canCombine) {
        selectBundledOption(dateKey, selectedOption);
      }
      return;
    }

    // Exit order bundling state if user selected a non bundled option.
    dispatch(exitOrderBundling());

    // If delivery method is mail, then delivery windows are not
    // applicable. Set order date and do not show window selection modal.
    if (isMailOrder) {
      const mailFee = selectedOption?.available_fees?.[0];
      trackMailSelected({
        fee_type: mailFee?.fee_type,
        fee_cohort: mailFee?.fee_cohort,
        selected_date: selectedDate,
      });
      // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
      dispatch(updateOrder({ date: selectedDate, delivery_method: deliveryMethod?.name }));

      return;
    }

    // If there is only a single fulfillment option for the date auto-select it
    if (selectedOption?.fulfillment_window_option?.delivery_window) {
      const { delivery_window: window, available_fee } = selectedOption.fulfillment_window_option;
      const isAsap = available_fee?.fee_type === 'asap_delivery';

      trackWindowSelected({
        autoSelected: true,
        deliver_after: window.start_time,
        deliver_before: window.end_time,
        fee_type: available_fee?.fee_type,
        fee_cohort: available_fee?.fee_cohort,
      });

      dispatch(updateOrder({ date: selectedDate, is_asap: isAsap }));
      dispatch(
        updateOrder({
          deliver_after: window.start_time,
          deliver_before: window.end_time,
          delivery_method: deliveryMethod?.name || null,
        }),
      );

      // Only clear bundling once a date has been selected
      if (selectedOption.tag !== 'BUNDLED') {
        dispatch(exitOrderBundling());
      }
      return;
    }

    // If not bundled and not pickup, show the action sheet to select a delivery window from the selected date.
    if (!isBundledOption && !isPickupOption) {
      setActiveActionSheet(
        <SelectDeliveryWindowActionSheet
          deliveryDate={selectedDate}
          selectedWindowOption={selectedFulfillmentWindowOption}
          handleDeliveryWindowSelection={handleStandardDeliveryWindowSelection}
        />,
      );
    }
  };

  const handleCheckoutPickupToggle = (value: OptionValue) => {
    // Toggle to delivery
    if (value !== PICKUP_VALUE) {
      trackEvent({
        event: EVENTS.CHECKOUT__DELIVERY_TOGGLED,
        params: {
          origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        },
      });
      dispatch(
        updateOrder({
          date: undefined,
          delivery_method: deliveryMethod?.name || null,
          pickup_address: undefined,
          pickup_ready_time: undefined,
          pay_at_pickup: undefined,
          home_to_sign_confirmation: undefined,
          deliver_after: undefined,
          deliver_before: undefined,
        }),
      );
      return;
    }

    // Toggle to pickup
    // Display and directly select the same-day pickup option since it's the only pickup option available for now
    const selectedSameDayPickupOption = pickupFulfillmentOptions[0];
    trackEvent({
      event: EVENTS.CHECKOUT__PICKUP_TOGGLED,
      params: {
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        ready_by_time: selectedSameDayPickupOption?.pickup_data?.ready_by_time,
      },
    });
    if (!selectedSameDayPickupOption) {
      return;
    }

    if (!selectedSameDayPickupOption.date) {
      return;
    }
    dispatch(
      updateOrder({
        date: selectedSameDayPickupOption.date,
        delivery_method: 'pickup',
        pickup_address: selectedSameDayPickupOption.pickup_data?.facility_address,
        pickup_ready_time: selectedSameDayPickupOption.pickup_data?.ready_by_time,
        deliver_after: undefined,
        deliver_before: undefined,
        tip_amount: undefined,
      }),
    );
  };

  if (isFulfillmentOptionsLoading) {
    return (
      <Card title="Schedule delivery">
        <Row centerHorizontally>
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            <AltoSpinningLoader />
          </LgPadding>
        </Row>
      </Card>
    );
  }

  return (
    <Card title={cardTitle}>
      {showPickupToggle ? (
        <>
          <LgPadding bottomPadding={SPACING.STATIC.NONE}>
            <ButtonSelector
              testID="checkout-pickup-selector"
              onChange={handleCheckoutPickupToggle}
              options={pickupToggleOptions}
              value={isOrderPickupMethod ? PICKUP_VALUE : DELIVERY_VALUE}
            />
          </LgPadding>
          {!isOrderPickupMethod ? (
            <LgPadding
              topPadding={isWeb ? SPACING.STATIC.XS : SPACING.STATIC.LG}
              bottomPadding={SPACING.STATIC.MD}
            >
              <ListHeader
                title="Select delivery date"
                fullBleed
              />
            </LgPadding>
          ) : null}
        </>
      ) : null}
      {showDeliveryOptions ? (
        <>
          <InputRadioGroup
            value={checkedValue}
            onValueChange={handleRadioPress}
            disabled={disableSelector}
          >
            {radioDateOptions.map((fulfillmentOption) => {
              return (
                <FulfillmentOptionRadioOption
                  key={`radio-${fulfillmentOption.date}-${fulfillmentOption.tag}-${fulfillmentOption.fee_amount_label}`}
                  fulfillmentOption={fulfillmentOption}
                  disableSelector={disableSelector}
                  loading={isFulfillmentOptionsLoading}
                  selectedWindowOption={selectedFulfillmentWindowOption}
                  deliveryMethod={deliveryMethod}
                  checkedValue={checkedValue}
                />
              );
            })}
          </InputRadioGroup>
          {/* Show "Select other date" only for the first shipment; second shipment cannot change date (single date sent to /v3/deliveries). */}
          {isFirstShipment && anyDateOption ? (
            <React.Fragment key={'any-day-option'}>
              <ListItem
                LeftContent={<Icon name="calendar" />}
                onPress={() => {
                  setActiveActionSheet(
                    <SelectCustomDateActionSheet
                      minDate={anyDateOption?.date || ''}
                      orderBundlingDates={orderBundlingDates}
                      handleDateSelection={handleCustomDateSelection}
                      openGetItSoonerFlow={openGetItSoonerFlow}
                    />,
                  );
                }}
                RightContent={
                  <FeeRadioRightContent
                    feeAmountLabel={anyDateOption?.fee_amount_label || undefined}
                    feeOriginalAmountLabel={anyDateOption?.fee_original_amount_label || undefined}
                    loading={isFulfillmentOptionsLoading}
                    showIconOpenCalendar
                  />
                }
                title={anyDateOption?.label || ''}
                testID={'any-day-delivery'}
              />
            </React.Fragment>
          ) : null}
          <AddressInfo topPadding={SPACING.STATIC.MD} />
          {showErrors ? (
            <AddressInfoAlert
              type="error"
              topPadding={SPACING.STATIC.MD}
              bottomPadding={SPACING.STATIC.MD}
            >
              <Description>{errorMessage}</Description>
            </AddressInfoAlert>
          ) : (
            <LgSpacing />
          )}
        </>
      ) : null}
      {hasAvailablePickupOptions && !isPickupToggleEnabled ? (
        <>
          <Divider />
          <LgPadding
            topPadding={SPACING.STATIC.MD}
            bottomPadding={SPACING.STATIC.MD}
          >
            <H2>Pick up at Alto</H2>
          </LgPadding>
          <InputRadioGroup
            value={checkedValue}
            onValueChange={handleRadioPress}
            disabled={disableSelector}
          >
            {pickupFulfillmentOptions.map((fulfillmentOption) => {
              return (
                <FulfillmentOptionRadioOption
                  key={`radio-${fulfillmentOption.date}-${fulfillmentOption.tag}-${fulfillmentOption.fee_amount_label}`}
                  fulfillmentOption={fulfillmentOption}
                  disableSelector={disableSelector}
                  loading={isFulfillmentOptionsLoading}
                />
              );
            })}
            {checkedValue?.startsWith('PICKUP') ? (
              <PickupInfo
                pickupData={
                  pickupFulfillmentOptions.find((pickupOption) => checkedValue === `PICKUP-${pickupOption.date}`)
                    ?.pickup_data
                }
              />
            ) : null}
          </InputRadioGroup>
          <SmSpacing />
        </>
      ) : null}
      {hasAvailablePickupOptions && isPickupToggleEnabled ? (
        <PickupOptionsSection
          pickupFulfillmentOptions={pickupFulfillmentOptions}
          isFulfillmentOptionsLoading={isFulfillmentOptionsLoading}
        />
      ) : null}
      {gskForYouHasOnlyStandardMailFee && isMailOrder && !isOrderPickupMethod ? <GskSpeedUpSection /> : null}
    </Card>
  );
};
